/**
 * AppSecurityCrypto.js
 * High-security zero-knowledge cryptographic helpers for per-app authentication.
 * Uses Web Crypto API (SubtleCrypto).
 * RAW PINs, PASSWORDS, AND PATTERNS ARE NEVER STORED.
 */

const PBKDF2_ITERATIONS = 100000;
const HASH_ALGO = 'SHA-256';

/**
 * Generate cryptographically secure random 16-byte salt as hex string
 */
export function generateSalt(byteLength = 16) {
  const array = new Uint8Array(byteLength);
  window.crypto.getRandomValues(array);
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convert string or dot pattern array into bytes
 */
function toBytes(data) {
  if (Array.isArray(data)) {
    // Pattern array e.g. [0, 1, 2, 4, 7]
    return new TextEncoder().encode(`cv_pattern:${data.join('-')}`);
  }
  return new TextEncoder().encode(String(data));
}

/**
 * Hash credential (PIN, pattern, or custom password) with salt using PBKDF2
 */
export async function hashCredential(secret, saltHex) {
  if (!secret) throw new Error('Secret is required for hashing');
  const saltBytes = new Uint8Array(
    saltHex.match(/.{1,2}/g).map((byte) => parseInt(byte, 16))
  );

  const secretBytes = toBytes(secret);

  const baseKey = await window.crypto.subtle.importKey(
    'raw',
    secretBytes,
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  const derivedBits = await window.crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: PBKDF2_ITERATIONS,
      hash: HASH_ALGO,
    },
    baseKey,
    256 // 32 bytes
  );

  const derivedArray = new Uint8Array(derivedBits);
  return Array.from(derivedArray)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Constant-time string comparison to prevent timing attacks
 */
function timingSafeEqual(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

/**
 * Verify a candidate credential against stored salt and expected hash
 */
export async function verifyCredential(inputSecret, saltHex, expectedHash) {
  try {
    if (!inputSecret || !saltHex || !expectedHash) return false;
    const computed = await hashCredential(inputSecret, saltHex);
    return timingSafeEqual(computed, expectedHash);
  } catch (err) {
    console.error('Credential verification error:', err);
    return false;
  }
}

/**
 * Serialize pattern nodes to a canonical string for debugging/display
 */
export function formatPatternSequence(nodes) {
  if (!Array.isArray(nodes)) return '';
  return nodes.join(' → ');
}
